"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeroSection from "./components/BayHarborHero";
import NeighborhoodStats from "@/components/feature/NeighborhoodStats";
import RepairServicesGrid from "@/components/feature/RepairServicesGrid";
import ScopeDisclaimer from "@/components/feature/ScopeDisclaimer";
import NeighborhoodCta from "@/components/feature/NeighborhoodCta";
import QuartierCrossLinks from "@/components/feature/QuartierCrossLinks";
import { injectMetaTags } from "@/lib/seoHelpers";
import FaqSection from "@/components/feature/FaqSection";
import RelatedServicesBlock from "@/components/feature/RelatedServicesBlock";
import NearbyAreasBlock from "@/components/feature/NearbyAreasBlock";

const neighborhood = "Bay Harbor Islands";
const zips = ["33154", "33141"];

export default function BayHarborPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const bayHarborFaqs = [
    { question: t("hood.faq1Q"), answer: t("hood.faq1A") },
    { question: t("hood.faq2Q"), answer: t("hood.faq2A") },
    { question: t("hood.faq3Q"), answer: t("hood.faq3A") },
    { question: t("hood.faq4Q"), answer: t("hood.faq4A") },
    { question: t("bayHarbor.faq5Q"), answer: t("bayHarbor.faq5A") },
  ];

  useEffect(() => {
    document.title = currentLang === "es"
      ? "Reparaci\u00F3n de Ventanas Bay Harbor Islands \u2014 Isla Frente a Playa"
      : "Bay Harbor Window Repair | Beachfront Salt Air Specialists";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = currentLang === "es"
      ? "Especialistas en reparaci\u00F3n de ventanas en Bay Harbor Islands. Experiencia en aire salino de isla frente a playa. Herrajes acero inoxidable calidad marina y planes semestrales Miami-Dade."
      : "Bay Harbor Islands window repair specialists. Beachfront island salt air expertise. Marine-grade stainless hardware and twice-yearly maintenance plans Miami-Dade.";

    const cleanupSeo = injectMetaTags({
      title: currentLang === "es"
        ? "Reparaci\u00F3n de Ventanas Bay Harbor Islands \u2014 Isla Frente a Playa"
        : "Bay Harbor Window Repair | Beachfront Salt Air Specialists",
      description: currentLang === "es"
        ? "Especialistas en reparaci\u00F3n de ventanas en Bay Harbor Islands. Experiencia en aire salino de isla frente a playa. Herrajes acero inoxidable calidad marina y planes semestrales Miami-Dade."
        : "Bay Harbor Islands window repair specialists. Beachfront island salt air expertise. Marine-grade stainless hardware and twice-yearly maintenance plans Miami-Dade.",
      url: "https://impact-window-repair-miami.com/impact-windows-bay-harbor-islands/",
      image: "https://impact-window-repair-miami.com/og/og-bay-harbor.jpg",
      imageAlt: "Window repair Bay Harbor Islands Miami",
      breadcrumbs: [
        { name: currentLang === "es" ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: currentLang === "es" ? "\u00C1reas de Servicio" : "Service Areas", item: "https://impact-window-repair-miami.com/service-areas/" },
        { name: "Bay Harbor Islands", item: "https://impact-window-repair-miami.com/impact-windows-bay-harbor-islands/" }
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: "Window Repair",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              "latitude": 25.7617,
              "longitude": -80.1918
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              "opens": "07:00",
              "closes": "21:00"
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              {"@type": "City", "name": "Miami"},
              {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee."
          },
          areaServed: {
            "@type": "City",
            name: "Bay Harbor Islands, Miami-Dade, FL",
          },
          serviceType: "Window Repair",
          description: currentLang === "es"
            ? "Reparaci\u00F3n de herrajes, reemplazo de vidrios y mantenimiento en Bay Harbor Islands."
            : "Hardware repair, glass replacement, and maintenance in Bay Harbor Islands.",
          inLanguage: currentLang === "es" ? "es-US" : "en-US",
        },
        {
          "@type": "LocalBusiness",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            "latitude": 25.7617,
            "longitude": -80.1918
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "07:00",
            "closes": "21:00"
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            {"@type": "City", "name": "Miami"},
            {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: bayHarborFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      const existingFaq = document.getElementById("faq-schema");
      if (existingFaq && existingFaq.parentNode) {
        document.head.removeChild(existingFaq);
      }
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeroSection h1={t("bayHarbor.h1")} subhead={t("bayHarbor.subhead")} zips={zips} />
      <NeighborhoodStats />
      <RepairServicesGrid heading={t("hood.commonH2")} />
      <ScopeDisclaimer neighborhood={neighborhood} />
      <FaqSection items={bayHarborFaqs} heading={t("hood.commonFaqH2")} />
      <RelatedServicesBlock neighborhood={neighborhood} />
      <NearbyAreasBlock neighborhood={neighborhood} neighbors={[
        { name: "Miami Beach", slug: "miami-beach" },
        { name: "Aventura", slug: "aventura" },
        { name: "North Miami Beach", slug: "north-miami-beach" },
      ]} />
      <NeighborhoodCta neighborhood={neighborhood} heading={t("hood.commonCta")} />
      <QuartierCrossLinks />
    </main>
  );
}